import {
  Box,
  Card,
  CardContent,
  Stack,
  ButtonGroup,
  Button,
  TableContainer,
  Table,
  TableHead,
} from "@mui/material";
import { mapOptional, pickListTableParams, useRequireParams } from "@/utils";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";

import { getCustomers } from "@/services/customer";

import { AddLeadDialog } from "./AddLeadDialog";

import { QueryTextField } from "@/components/QueryTextField";
import { ItemsTotal } from "@/components/ItemsTotal";
import { SearchParamsPagination } from "@/components/SearchParamsPagination";
import { formatCustomerStatus } from "@/formatter";
import { CustomerStatus } from "@/models";
import { DataTableBody } from "@/components/DataTableBody";

import { CustomerTableRowHeader, CustomerTableRow } from "./CustomerTableRow";

const QUERY_KEY = "customer";

export function CustomerListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { saleId: id } = useRequireParams(["saleId"]);
  const query = pickListTableParams(searchParams);
  const { data: raw, isFetching } = useQuery([QUERY_KEY, query], () =>
    getCustomers({ saleId: id, query })
  );

  const data = raw?.data ?? [];
  const total = raw?.total;

  const dialog = searchParams.get("dialog");

  const status = searchParams.get("status") as CustomerStatus | undefined;
  const setStatus = (s: CustomerStatus | null) => () => {
    if (s) {
      searchParams.set("status", s);
    } else {
      searchParams.delete("status");
    }

    searchParams.delete("page");
    setSearchParams(searchParams);
  };

  function add() {
    searchParams.set("dialog", "lead-add");
    setSearchParams(searchParams);
  }

  function onCloseDialog() {
    searchParams.delete("dialog");
    setSearchParams(searchParams, { replace: true });
  }

  const addLeadDialog = {
    open: dialog === "lead-add",
    onClose: onCloseDialog,
    fetchKeys: [QUERY_KEY],
    saleId: id,
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              mb={3}
            >
              <QueryTextField
                sx={{ width: 340 }}
                placeholder="ค้นหาชื่อ หรือเบอร์โทรศัพท์"
              />
              <ButtonGroup variant="contained" color="inherit">
                {[
                  null,
                  CustomerStatus.Lead,
                  CustomerStatus.Prospect,
                  CustomerStatus.Member,
                  CustomerStatus.Close,
                  CustomerStatus.Unqualified,
                ].map((s) => (
                  <Button
                    key={s}
                    onClick={setStatus(s)}
                    sx={{ bgcolor: s === status ? "grey.100" : "grey.300" }}
                  >
                    {mapOptional(s, formatCustomerStatus) ?? "ทั้งหมด"}
                  </Button>
                ))}
              </ButtonGroup>
              <Button variant="contained" onClick={add}>
                เพิ่มผู้ที่สนใจ
              </Button>
            </Stack>
            <ItemsTotal count={total} />
            <TableContainer sx={{ height: "600px", overflowX: "hidden" }}>
              <Table stickyHeader>
                <TableHead>
                  <CustomerTableRowHeader />
                </TableHead>
                <DataTableBody loading={isFetching} data={data}>
                  {data.map((item) => (
                    <CustomerTableRow key={item.id} data={item} />
                  ))}
                </DataTableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
      <SearchParamsPagination total={total} />
      <AddLeadDialog {...addLeadDialog} />
    </Box>
  );
}
